import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Button, Select, MenuItem, FormControl, InputLabel, CircularProgress, FormControlLabel, Switch } from "@mui/material";
import { Forum, WarningAmber } from "@mui/icons-material";
import SavingOverlay from "../components/SavingOverlay";
import { aiIntegrationService, PatientAssistantAgentOption } from "../services/aiIntegrationService";
import { voiceService, VoiceSettings } from "../services/voiceService";
import { colors, typography } from "../theme/designSystem";

interface Props {
  entityId: string;
  onToast: (message: string, type: "success" | "error") => void;
}

/**
 * Assistente do prontuário (item 206): a clínica escolhe o agente próprio do assistente. Quem usa é
 * o Profissional que tiver esse agente liberado em Agentes de IA — ter outro agente (a Ani, a
 * entrevista) não dá acesso ao prontuário. Sem agente, o item some do menu de todos.
 */
const PatientAssistantConfig: React.FC<Props> = ({ entityId, onToast }) => {
  const [options, setOptions] = useState<PatientAssistantAgentOption[]>([]);
  const [agentId, setAgentId] = useState("");
  const [speech, setSpeech] = useState(false);
  const [saved, setSaved] = useState({ agent: "", speech: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [voice, setVoice] = useState<VoiceSettings | null>(null);

  useEffect(() => {
    let alive = true;
    voiceService.getSettings().then((v) => { if (alive) setVoice(v); }).catch(() => undefined);
    return () => { alive = false; };
  }, [entityId]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    aiIntegrationService
      .getPatientAssistant(entityId)
      .then((data) => {
        if (!alive) return;
        setOptions(data.options);
        setAgentId(data.agentId || "");
        setSpeech(!!data.speechReply);
        setSaved({ agent: data.agentId || "", speech: !!data.speechReply });
      })
      .catch(() => { if (alive) onToast("Não foi possível carregar a configuração do assistente.", "error"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [entityId, onToast]);

  const selected = useMemo(() => options.find((o) => o.id === agentId), [options, agentId]);
  const dirty = agentId !== saved.agent || speech !== saved.speech;

  const handleAgentChange = (value: string) => {
    setAgentId(value);
    // Sem agente não há assistente, nem voz
    if (!value) setSpeech(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await aiIntegrationService.savePatientAssistant(entityId, agentId || null, speech);
      setSaved({ agent: agentId, speech });
      onToast(agentId ? "Assistente do prontuário configurado." : "Assistente do prontuário desligado.", "success");
    } catch {
      onToast("Não foi possível salvar a configuração do assistente.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}><CircularProgress size={22} /></Box>;
  }

  return (
    <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #e9ecef" }}>
      <SavingOverlay open={saving} />
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Forum sx={{ color: colors.primary }} />
        <Typography sx={{ fontWeight: 600, color: colors.textPrimary }}>Assistente do prontuário</Typography>
      </Box>
      <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2, maxWidth: "56rem" }}>
        O agente escolhido conduz as conversas presas a um paciente. Só o perfil <strong>Profissional</strong> usa, e
        só quem tiver este agente liberado em <strong>Tecnologia &gt; Agentes de IA &gt; Usuários</strong> vê o item no
        menu. Deixe em branco para desligar o assistente.
      </Typography>

      <FormControl size="small" sx={{ minWidth: "18rem", mb: 2 }}>
        <InputLabel id="patient-assistant-agent">Agente do assistente</InputLabel>
        <Select labelId="patient-assistant-agent" label="Agente do assistente" value={agentId} onChange={(e) => handleAgentChange(e.target.value)}>
          <MenuItem value=""><em>Desligado</em></MenuItem>
          {options.map((o) => <MenuItem key={o.id} value={o.id}>{o.displayName}</MenuItem>)}
        </Select>
      </FormControl>

      {options.length === 0 && (
        <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
          Nenhum agente elegível. O assistente lê o prontuário do paciente, então só aceita agente com escopo de dados
          <strong> próprios</strong>. Crie um agente para o assistente em Agentes de IA.
        </Typography>
      )}

      {selected && selected.assignedUsers === 0 && (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, backgroundColor: "#fff3cd", border: "1px solid #ffe69c", borderRadius: "8px", p: "0.75rem 1rem", mb: 2, maxWidth: "56rem" }}>
          <WarningAmber sx={{ color: "#856404", fontSize: 20, mt: "2px" }} />
          <Typography variant="body2" sx={{ color: "#856404", fontSize: typography.fontSize.sm }}>
            <strong>{selected.displayName}</strong> não está liberado para ninguém. Libere para cada profissional em
            <strong> Tecnologia &gt; Agentes de IA &gt; Usuários</strong>; sem isso o assistente não aparece no menu.
          </Typography>
        </Box>
      )}

      {voice && (
        <Box sx={{ mb: 2, maxWidth: "56rem" }}>
          <FormControlLabel
            control={<Switch checked={speech} disabled={saving || !agentId || (!voice.speechReady && !speech)}
              onChange={(e) => setSpeech(e.target.checked)} />}
            label={<Typography sx={{ fontSize: typography.fontSize.sm, fontWeight: 600 }}>Modo conversa (resposta falada)</Typography>}
          />
          <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: typography.fontSize.sm }}>
            Libera o botão de modo conversa no Assistente do prontuário: o profissional fala, o assistente responde em voz e
            volta a ouvir. A voz é gerada no servidor da clínica, o texto não sai dele e o áudio não é guardado. A resposta
            sai pelo alto-falante: use em ambiente reservado.
          </Typography>
          {!voice.speechReady && (
            <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: typography.fontSize.sm, mt: 0.5 }}>
              A voz ainda não está disponível neste ambiente.
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ mb: 1 }}>
        <Button onClick={handleSave} disabled={saving || !dirty} variant="contained" sx={{ textTransform: "none", backgroundColor: colors.primary }}>
          Salvar assistente
        </Button>
      </Box>
    </Box>
  );
};

export default PatientAssistantConfig;
